import {
  Badge,
  Checkbox,
  ErrorBanner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
} from "@lumanity/ui/primitives";
import { WORKFLOW_ROLES, WORKFLOW_ROLE_LABELS, toggleRole, useSetWorkflowRoles, useTeam, type TeamMember, type WorkflowRole } from "./teamApi";

/**
 * Who writes and who does Senior QC (FR-1-03). Membership comes from the
 * project setup; owners and admins tick each member's workflow roles here. A
 * member may hold both — on any one output, whoever wrote it can't QC it.
 * Roles are stored on the project in the project service, and every change is
 * recorded in the audit trail.
 */
const TeamRoles = ({ projectId }: { projectId: string }) => {
  const { data: team, isLoading, error } = useTeam(projectId);
  const setRoles = useSetWorkflowRoles(projectId);

  if (error) return <ErrorBanner message={`Could not load the team: ${error.message}`} />;
  if (isLoading || !team) return <div className="text-muted-foreground py-4">Loading team...</div>;

  const handleToggle = (member: TeamMember, role: WorkflowRole, on: boolean) =>
    setRoles.mutate(
      { userId: member.user_id, roles: toggleRole(member.workflow_roles, role, on) },
      { onError: (e) => toast({ title: "Could not change the roles", description: e.message, variant: "destructive" }) },
    );

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Name</TableHead>
          <TableHead>Email</TableHead>
          <TableHead className="w-28">Access</TableHead>
          {WORKFLOW_ROLES.map((role) => (
            <TableHead key={role} className="w-36 text-center">
              {WORKFLOW_ROLE_LABELS[role]}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {team.members.map((m) => (
          <TableRow key={m.user_id}>
            <TableCell className="font-medium">
              {m.first_name} {m.last_name}
            </TableCell>
            <TableCell className="text-muted-foreground">{m.email}</TableCell>
            <TableCell>
              <Badge variant={m.role === "owner" ? "default" : "secondary"} className="capitalize">
                {m.role}
              </Badge>
            </TableCell>
            {WORKFLOW_ROLES.map((role) => {
              const label = `${WORKFLOW_ROLE_LABELS[role]}: ${m.first_name} ${m.last_name}`;
              const held = m.workflow_roles.includes(role);
              return (
                <TableCell key={role} className="text-center">
                  {team.can_manage ? (
                    <Checkbox
                      aria-label={label}
                      checked={held}
                      disabled={setRoles.isPending}
                      onCheckedChange={(checked) => handleToggle(m, role, checked === true)}
                    />
                  ) : (
                    <span aria-label={label}>{held ? "✓" : "—"}</span>
                  )}
                </TableCell>
              );
            })}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export default TeamRoles;
